import { Link } from 'react-router-dom';
import { ShieldCheck, Eye, Lock, Database, UserCircle, ArrowRight, Trash } from '@phosphor-icons/react';
import Breadcrumbs from '../components/Breadcrumbs';
import AppFooter from '../components/AppFooter';
import PageMeta from '../components/PageMeta';

export default function Trust() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white dark:from-gray-900 dark:to-gray-900">
      <PageMeta
        title="Trust & Safety"
        description="How Romy protects your health data. Learn about our privacy practices, encryption, data ownership, and commitment to women's health safety."
        path="/trust"
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <Breadcrumbs current="Trust & Safety" />

        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck size={32} className="text-pink-500" weight="fill" />
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 dark:text-gray-100">How We Handle Your Data</h1>
        </div>
        <p className="text-gray-500 dark:text-gray-400 mb-10">Transparency about what we collect, how we use it, and how you stay in control.</p>

        <div className="space-y-8">
          {/* Built Different */}
          <section className="bg-white dark:bg-gray-900 rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3">Built Different</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              Romy is purpose-built for women's health &mdash; not a generic AI with a health skin. This means every design decision prioritizes your safety and privacy.
            </p>
            <ul className="space-y-2 text-gray-700 dark:text-gray-300">
              <li className="flex items-start gap-2">
                <span className="text-pink-500 mt-1 flex-shrink-0">&bull;</span>
                <span><strong>No data selling.</strong> Your health data is never sold, licensed, or shared with advertisers.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-pink-500 mt-1 flex-shrink-0">&bull;</span>
                <span><strong>No model training on your data.</strong> Your personal conversations are not used to train AI models.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-pink-500 mt-1 flex-shrink-0">&bull;</span>
                <span><strong>Research data is always anonymized.</strong> We may use anonymized, aggregate data for research — your identity is never connected to it.</span>
              </li>
            </ul>
          </section>

          {/* What We Collect, Process, Share */}
          <section className="bg-white dark:bg-gray-900 rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">What We Collect &rarr; Process &rarr; Share</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-pink-50 dark:bg-pink-950/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Eye size={20} className="text-pink-500" />
                  <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">Collect</h3>
                </div>
                <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  <li>Chat messages</li>
                  <li>Health logs you enter</li>
                  <li>Profile info (age, conditions)</li>
                  <li>Community posts</li>
                  <li>Basic usage analytics</li>
                </ul>
              </div>
              <div className="bg-orange-50 dark:bg-orange-950/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Database size={20} className="text-orange-500" />
                  <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">Process</h3>
                </div>
                <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  <li>AI generates responses</li>
                  <li>Patterns detected from logs</li>
                  <li>Long-term memory summaries</li>
                  <li>Artifact generation</li>
                  <li>Community insights (aggregated)</li>
                </ul>
              </div>
              <div className="bg-green-50 dark:bg-green-950/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Lock size={20} className="text-green-500" />
                  <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">Share</h3>
                </div>
                <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  <li>Google Gemini (for AI responses)</li>
                  <li>Anonymized research (always anonymized)</li>
                  <li className="font-medium text-green-700 dark:text-green-400">Nothing else.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Data Retention */}
          <section className="bg-white dark:bg-gray-900 rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3">How Long We Keep Things</h2>
            <ul className="space-y-3 text-gray-700 dark:text-gray-300">
              <li className="flex items-start gap-2">
                <span className="text-pink-500 mt-1 flex-shrink-0">&bull;</span>
                <div>
                  <strong>Uploaded images: 48 hours.</strong> When you share a photo (lab result, prescription, etc.) in chat, the raw image is auto-deleted after 48 hours. Anything Romy extracted &mdash; lab values, medications, treatments &mdash; is saved separately to your health record and stays as long as you want it to. We delete the raw images on purpose: less stored data about your body, your labs, and your prescriptions means less to lose.
                </div>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-pink-500 mt-1 flex-shrink-0">&bull;</span>
                <div>
                  <strong>Chat messages, health logs, treatments, labs:</strong> kept as long as your account is active. Deleting your account removes them.
                </div>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-pink-500 mt-1 flex-shrink-0">&bull;</span>
                <div>
                  <strong>Anonymized research data:</strong> persists across account deletion (your identity isn't in it), but only research consent participants are ever included.
                </div>
              </li>
            </ul>
          </section>

          {/* Your Controls */}
          <section className="bg-white dark:bg-gray-900 rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3">Your Controls</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              You have full control over your data at all times.
            </p>
            <div className="space-y-3">
              <Link
                to="/settings/your-data"
                className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <UserCircle size={20} className="text-pink-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-100">View, Download, or Delete Your Data</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">See everything we store and take action</p>
                  </div>
                </div>
                <ArrowRight size={16} className="text-gray-400" />
              </Link>
              <Link
                to="/account/delete"
                className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Trash size={20} className="text-red-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-100">Delete Your Account</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Permanently remove your account and all data</p>
                  </div>
                </div>
                <ArrowRight size={16} className="text-gray-400" />
              </Link>
            </div>
          </section>

          {/* Technical Safeguards */}
          <section className="bg-white dark:bg-gray-900 rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3">Technical Safeguards</h2>
            <ul className="space-y-2 text-gray-700 dark:text-gray-300">
              <li className="flex items-start gap-2">
                <Lock size={16} className="text-green-500 mt-1 flex-shrink-0" />
                <span><strong>Firebase Auth</strong> &mdash; secure Google OAuth and email/password authentication</span>
              </li>
              <li className="flex items-start gap-2">
                <Lock size={16} className="text-green-500 mt-1 flex-shrink-0" />
                <span><strong>Firestore Security Rules</strong> &mdash; server-enforced access control ensures you can only read your own data</span>
              </li>
              <li className="flex items-start gap-2">
                <Lock size={16} className="text-green-500 mt-1 flex-shrink-0" />
                <span><strong>App Check</strong> &mdash; protects API endpoints from unauthorized access and bots</span>
              </li>
              <li className="flex items-start gap-2">
                <Lock size={16} className="text-green-500 mt-1 flex-shrink-0" />
                <span><strong>Anonymization</strong> &mdash; research data is hashed and stripped of personally identifiable information</span>
              </li>
              <li className="flex items-start gap-2">
                <Lock size={16} className="text-green-500 mt-1 flex-shrink-0" />
                <span><strong>HTTPS Everywhere</strong> &mdash; all data in transit is encrypted via TLS</span>
              </li>
            </ul>
          </section>

          {/* Questions */}
          <section className="bg-white dark:bg-gray-900 rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3">Questions?</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              Read our full{' '}
              <Link to="/privacy" className="text-pink-600 dark:text-pink-400 hover:text-pink-700 dark:hover:text-pink-300 underline">
                Privacy Policy
              </Link>{' '}
              for legal details, or reach out at{' '}
              <a href="mailto:hello@romyhealth.co" className="text-pink-600 dark:text-pink-400 hover:text-pink-700 dark:hover:text-pink-300 underline">
                hello@romyhealth.co
              </a>.
            </p>
          </section>
        </div>
      </div>

      <AppFooter />
    </div>
  );
}
